import Styles from './styles.module.css';
import React, { useRef, useState } from 'react';
import { IoImageOutline } from "react-icons/io5";
import { v4 as uuid } from 'uuid';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { getFirestore, setDoc, doc, updateDoc } from 'firebase/firestore';
import { defaultStoreData, storeData } from 'src/type';

type StoreMakeProps = {
  companyId: string;
  documentId: string;
  setMakeStore: React.Dispatch<React.SetStateAction<boolean>>;
  length: number;
  setStoreImageLocal: React.Dispatch<React.SetStateAction<string>>;
};

const StoreMake: React.FC<StoreMakeProps> = ({ companyId, documentId, setMakeStore, length, setStoreImageLocal }) => {

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [ name, setName ] = useState('');

  const [ errorPhoto, setErrorPhoto ] = useState(false);
  const [ errorName, setErrorName ] = useState(false);

  // 保存時のバリデーション結果を1件だけ上部に表示する
  const [ errorMessage, setErrorMessage ] = useState<string>('');

  const showError = (message: string) => {
    setErrorMessage(message);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  const [ uploading, setUploading ] = useState(false);

  const [ styleImageFile, setStyleImageFile ] = useState('');
  const [ styleImageFilePath, setStyleImageFilePath ] = useState<File | null>(null);

  // =========================
  // UIロック状態（本質）
  // =========================
  const isLocked = uploading;

  // =========================
  // 画像選択
  // =========================
  const styleImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return;
    if (!e.target.files?.[0]) return;

    const file = e.target.files[0];

    setStyleImageFile(URL.createObjectURL(file));
    setStyleImageFilePath(file);
  };

  // =========================
  // 保存処理（完全安全版）
  // =========================
  async function save() {
    if (isLocked) return;

    setErrorPhoto(false);
    setErrorName(false);
    setErrorMessage('');

    if (!styleImageFilePath) {
      setErrorPhoto(true);
      showError('店舗写真を追加してください。');
      return;
    }

    if (!name) {
      setErrorName(true);
      showError('店舗名を入力してください。');
      return;
    }

    const docId = uuid();

    setUploading(true);

    const storeDataToSave: storeData = {
      ...defaultStoreData,
      companyId,
      recruitId: documentId,
      number: length + 1,
      image: docId,
      name,
      time: new Date(),
      last_time: new Date(),
    };

    const db = getFirestore();
    const storage = getStorage();

    try {
      // =========================
      // ① Firestore先に作成（元の挙動維持）
      // =========================
      await setDoc(doc(db, 'stores', docId), storeDataToSave);

      // =========================
      // ② 通常画像アップロード
      // =========================
      const storageRef = ref(storage, `/stores/${docId}`);
      await uploadBytes(storageRef, styleImageFilePath);

      // =========================
      // ③ recruit更新（元の条件そのまま）
      // =========================
      await updateDoc(doc(db, "recruits", documentId), {
        store: { isCompleted: false },
      });

      // =========================
      // ④ length === 0 の特殊処理（完全復元）
      // =========================
      if (styleImageFilePath && length === 0) {
        const jobStorageRef = ref(storage, `/jobPostingsStore/${docId}`);

        try {
          const fileToUpload = length === 0 ? await cropImageToCoverBlob(styleImageFilePath, 1200, 800) : styleImageFilePath;

          setStoreImageLocal(styleImageFile);
          await uploadBytes(jobStorageRef, fileToUpload);

        } catch (error) {
          console.error("画像クロップ or jobPostingsStore失敗", error);
        } finally {
        }
      }

      // =========================
      // ⑤ UI反映・クローズ（元の位置維持）
      // =========================
      setMakeStore(false);

    } catch (error) {
      console.error(error);
      setErrorPhoto(true);
    } finally {
      setUploading(false);
    }
  }

  const cropImageToCoverBlob = (
    file: File,
    targetWidth: number,
    targetHeight: number
  ): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result !== "string") return reject("Invalid file");

        img.src = reader.result;
        img.crossOrigin = "anonymous";

        img.onload = () => {
          const imgAspect = img.width / img.height;
          const targetAspect = targetWidth / targetHeight;

          let sx = 0,
            sy = 0,
            sWidth = img.width,
            sHeight = img.height;

          if (imgAspect > targetAspect) {
            sWidth = img.height * targetAspect;
            sx = (img.width - sWidth) / 2;
          } else {
            sHeight = img.width / targetAspect;
            sy = (img.height - sHeight) / 2;
          }

          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          const ctx = canvas.getContext("2d");
          if (!ctx) return reject("No canvas context");

          ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);

          canvas.toBlob((blob) => {
            if (!blob) return reject("Canvas toBlob failed");
            resolve(blob);
          }, "image/png");
        };

        img.onerror = reject;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true'>
      <div className={Styles.scrim} onClick={() => !isLocked && setMakeStore(false)} />
      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>店舗追加</h3>
          <button type='button' className={Styles.headerBtn} onClick={save} disabled={isLocked}>
            {isLocked ? '処理中…' : '確定'}
          </button>
        </div>

        <div className={Styles.body} ref={bodyRef}>

          {errorMessage && (
            <div className={Styles.errorBox} role='alert'>
              <p className={Styles.errorBox_label}>エラーメッセージ：</p>
              <p className={Styles.errorBox_text}>{errorMessage}</p>
            </div>
          )}

          {/* 店舗写真 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>店舗写真<span className={Styles.req}>※</span></span>
            </div>
            <label className={errorPhoto ? `${Styles.imageBox} ${Styles.imageBox_error}` : Styles.imageBox}>
              {styleImageFile ? (
                <img className={Styles.imagePreview} src={styleImageFile} alt="店舗写真" />
              ) : (
                <div className={Styles.imagePlaceholder}>
                  <IoImageOutline className={Styles.imagePlaceholderIcon} />
                  <span>写真を追加</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={styleImages}
                disabled={isLocked}
                className={Styles.hiddenInput}
              />
            </label>
          </div>

          {/* 店舗名 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>店舗名<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{name.length} / 20</span>
            </div>
            <input
              className={errorName ? `${Styles.input} ${Styles.input_error}` : Styles.input}
              placeholder="メモリー 横浜店"
              maxLength={20}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLocked}
            />
          </div>

        </div>

        {isLocked && (
          <div className={Styles.lockOverlay}>
            <div className={Styles.spinner} />
            <p>アップロード中...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreMake;
